/*
  Quick-add stepper for the tile product card (snippets/product-card-tile.liquid).

  "+ Add" is the theme's own product-form submit; once the variant is in the
  cart the pill morphs into a − qty + stepper that edits the line directly.
  A single shared cart snapshot is kept in sync with the theme's `cart:change`
  events (fired by product-form, cart drawer, Foodify, cc.liquid) so every
  card on the page — including ones appended later by infinite scroll or
  swapped in by facet:update — reflects the live quantity without extra
  requests. Only lines WITHOUT properties are counted: Foodify/customized
  lines are distinct configurations and keep their own popup flow.
*/
(function () {
  if (window.customElements.get('quick-add-stepper')) return;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // ---- shared cart snapshot -------------------------------------------
  var cartPromise = null;
  var byVariant = {};          // variantId -> { key, quantity }
  var listeners = new Set();   // stepper instances

  function index(cart) {
    byVariant = {};
    if (!cart || !Array.isArray(cart.items)) return;
    cart.items.forEach(function (line) {
      var props = line.properties || {};
      if (Object.keys(props).length > 0) return; // customized line — leave alone
      var id = String(line.variant_id);
      if (!byVariant[id]) byVariant[id] = { key: line.key, quantity: 0 };
      byVariant[id].quantity += line.quantity;
    });
    listeners.forEach(function (el) { el.render(); });
  }

  function loadCart() {
    if (!cartPromise) {
      cartPromise = fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) { index(cart); return cart; })
        .catch(function () { cartPromise = null; });
    }
    return cartPromise;
  }

  document.addEventListener('cart:change', function (event) {
    var cart = event.detail && event.detail.cart;
    if (cart) { cartPromise = Promise.resolve(cart); index(cart); }
  });
  document.addEventListener('cart:refresh', function () { cartPromise = null; loadCart(); });
  window.addEventListener('pageshow', function (event) { if (event.persisted) { cartPromise = null; loadCart(); } });

  // ---- element -----------------------------------------------------------
  class QuickAddStepper extends HTMLElement {
    connectedCallback() {
      this._variantId = this.getAttribute('data-variant-id');
      this._stepper = this.querySelector('.quick-add__stepper');
      this._qty = this.querySelector('.quick-add__qty');
      if (!this._onClick) {
        this._onClick = this._handleClick.bind(this);
        this.addEventListener('click', this._onClick);
      }
      listeners.add(this);
      loadCart();
      this.render();
    }

    disconnectedCallback() {
      listeners.delete(this);
    }

    render() {
      var line = byVariant[this._variantId];
      var inCart = !!(line && line.quantity > 0);
      if (inCart) {
        this.setAttribute('data-in-cart', '');
        if (this._stepper) this._stepper.removeAttribute('hidden');
        if (this._qty) this._qty.textContent = line.quantity;
      } else {
        this.removeAttribute('data-in-cart');
        if (this._stepper) this._stepper.setAttribute('hidden', '');
      }
    }

    _handleClick(event) {
      var step = event.target.closest && event.target.closest('.quick-add__step');
      if (!step || !this.contains(step)) return;
      event.preventDefault();
      event.stopPropagation();
      var line = byVariant[this._variantId];
      if (!line) return;
      var delta = parseInt(step.getAttribute('data-step'), 10) || 0;
      this._change(line.key, Math.max(0, line.quantity + delta));
    }

    async _change(lineKey, quantity) {
      if (this._busy) return;
      this._busy = true;
      if (this._stepper) this._stepper.setAttribute('aria-busy', 'true');
      try {
        // Same protocol as the theme's line-item-quantity: let the cart
        // drawer register its section, post the change, broadcast the cart.
        var sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true, detail: { sections: sections }
        }));
        var response = await fetch(root + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity: quantity, sections: sections })
        });
        var cart = await response.json();
        if (!response.ok) throw new Error(cart && cart.description || 'Cart change failed');
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true, detail: { baseEvent: 'line-item:change', cart: cart }
        }));
      } catch (e) {
        console.error('quick-add-stepper:', e);
        // Resync from the server so the UI never shows a stale quantity.
        cartPromise = null;
        loadCart();
      } finally {
        this._busy = false;
        if (this._stepper) this._stepper.removeAttribute('aria-busy');
      }
    }
  }

  window.customElements.define('quick-add-stepper', QuickAddStepper);

  // ---- size pills --------------------------------------------------------
  // Cards for products whose single option has several variants render pill
  // buttons under the price (see product-card-tile.liquid). A pill selects
  // its variant for the card: hidden form input, stepper identity, and the
  // displayed price all follow. Delegated so cloned/appended cards work.
  document.addEventListener('click', function (event) {
    var pill = event.target.closest && event.target.closest('[data-size-pill]');
    if (!pill || pill.disabled) return;
    var card = pill.closest('product-card');
    if (!card) return;
    event.preventDefault();
    event.stopPropagation();

    var variantId = pill.getAttribute('data-variant-id');
    card.querySelectorAll('[data-size-pill]').forEach(function (b) {
      var active = b === pill;
      b.classList.toggle('is-active', active);
      b.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    card.setAttribute('data-variant-id', variantId);
    var idInput = card.querySelector('form input[name="id"]');
    if (idInput) idInput.value = variantId;

    var stepper = card.querySelector('quick-add-stepper');
    if (stepper) {
      stepper.setAttribute('data-variant-id', variantId);
      stepper._variantId = variantId;
      if (typeof stepper.render === 'function') stepper.render();
    }

    var price = pill.getAttribute('data-variant-price');
    var priceEl = card.querySelector('.product-card__price-link sale-price');
    if (price && priceEl) {
      // Keep the sr-only label, replace the visible amount.
      var sr = priceEl.querySelector('.sr-only');
      priceEl.textContent = price;
      if (sr) priceEl.prepend(sr);
    }

    // Foodify cards: the options modal owns its pricing through hidden
    // native-variant radios (one per variant, data-variant-price on each) —
    // getPrice() re-derives its base from the CHECKED radio on every
    // recompute. Selecting the matching radio and firing change hands the
    // whole thing to foodify's own machinery.
    var radio = card.querySelector('input.native-variant[data-variant-id="' + variantId + '"]');
    if (radio && !radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
    var rawPrice = pill.getAttribute('data-variant-price-raw');
    if (rawPrice) card.setAttribute('data-product-price', rawPrice);
  }, { capture: true });
})();
